import { useState, useEffect, useCallback, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { UserPlus, ArrowLeft, Plus, Pencil, Trash2, Loader2, Eye, EyeOff, Upload, FileUp, Download, Check, X, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const EMPTY = {
  id: null, name: "", credentials: "", photo: "", bio: "",
  specialties: "", states: "", languages: "", insurance: "",
  modality: "telehealth", faith_informed: false, trauma_informed: false,
  booking_url: "", website: "", email: "", phone: "", published: true,
};

const toStr = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

export default function AdminTherapists() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [importing, setImporting] = useState(false);
  const photoRef = useRef(null);
  const csvRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/therapists/admin/list"); setRows(data); }
    catch { toast.error("Couldn't load providers."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => setDialog({ ...EMPTY });
  const openEdit = (t) => setDialog({
    ...EMPTY, ...t,
    specialties: toStr(t.specialties), states: toStr(t.states),
    languages: toStr(t.languages), insurance: toStr(t.insurance),
  });
  const setField = (k, v) => setDialog((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!dialog.name.trim()) { toast.error("Provider name is required."); return; }
    setSaving(true);
    try {
      await api.post("/therapists/admin/save", dialog);
      toast.success(dialog.id ? "Provider updated." : "Provider added.");
      setDialog(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const togglePublish = async (t) => {
    try {
      await api.post("/therapists/admin/save", { ...t, specialties: toStr(t.specialties), states: toStr(t.states), languages: toStr(t.languages), insurance: toStr(t.insurance), published: !t.published });
      load();
    } catch { toast.error("Couldn't update."); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    await api.delete(`/therapists/admin/${t.id}`);
    toast.success("Deleted.");
    load();
  };

  const approve = async (t) => {
    await api.post(`/therapists/admin/${t.id}/approve`);
    toast.success(`${t.name} approved & published.`);
    load();
  };
  const reject = async (t) => {
    if (!window.confirm(`Reject "${t.name}"? They won't appear in the directory.`)) return;
    await api.post(`/therapists/admin/${t.id}/reject`);
    toast.success("Application rejected.");
    load();
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image."); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/therapists/admin/upload-photo", form, { headers: { "Content-Type": "multipart/form-data" } });
      setField("photo", data.photo);
      toast.success("Photo uploaded.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const downloadTemplate = () => {
    const header = "name,credentials,photo,bio,specialties,states,languages,insurance,modality,faith_informed,trauma_informed,booking_url,website,email,phone,published";
    const sample = 'Dr. Maya Bennett,LCSW,,Warm culturally attuned care.,Anxiety;Racial Identity,GA;NY,English;Spanish,Aetna;Sliding Scale,both,false,true,https://example.com/book,,maya@example.com,,true';
    const blob = new Blob([header + "\n" + sample + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "therapists-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!bulkText.trim()) { toast.error("Paste CSV or choose a file."); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/therapists/admin/bulk", { csv: bulkText });
      const errs = data.errors || [];
      if (errs.length) toast.warning(`Imported ${data.created}. ${errs.length} row(s) skipped (row ${errs[0].row}: ${errs[0].error}).`);
      else toast.success(`Imported ${data.created} provider${data.created === 1 ? "" : "s"}.`);
      setBulk(false); setBulkText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed."); }
    finally { setImporting(false); }
  };

  if (user && user.role !== "owner") return <Navigate to="/home" replace />;

  const pending = rows.filter((r) => r.status === "pending");
  const others = rows.filter((r) => r.status !== "pending");
  const inviteLink = `${process.env.REACT_APP_PUBLIC_SITE_URL || window.location.origin}/therapists/apply`;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-therapists-page">
      <Link to="/settings" className="inline-flex items-center gap-2 text-sm text-[#59535B] hover:text-[#291B2D] transition-smooth" data-testid="admin-therapists-back">
        <ArrowLeft className="h-4 w-4" /> Back to settings
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <UserPlus className="text-[#C89B55]" size={22} />
          <div>
            <h1 className="text-2xl font-heading text-[#291B2D]">Therapists</h1>
            <p className="text-sm text-[#59535B]">Add and publish providers for the Find a Therapist directory.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setBulk(true)} data-testid="bulk-import-btn" className="inline-flex items-center gap-2 rounded-full bg-[#F7F2EA] text-[#291B2D] px-5 py-2.5 text-sm font-medium hover:brightness-95 transition-smooth">
            <FileUp size={16} /> Bulk import
          </button>
          <button onClick={openAdd} data-testid="add-therapist-btn" className="inline-flex items-center gap-2 rounded-full bg-[#291B2D] text-white px-5 py-2.5 text-sm font-medium hover:brightness-110 transition-smooth">
            <Plus size={16} /> Add provider
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#E5DED4] p-4 flex items-center gap-3 flex-wrap" data-testid="therapist-invite-link">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[#291B2D]">Invite link for therapists</p>
          <p className="text-xs text-[#8A838E] truncate">{inviteLink}</p>
        </div>
        <button
          onClick={() => { navigator.clipboard?.writeText(inviteLink); toast.success("Invite link copied."); }}
          data-testid="copy-invite-link"
          className="inline-flex items-center gap-2 rounded-full bg-[#F7F2EA] text-[#291B2D] px-4 py-2 text-sm font-medium hover:brightness-95"
        >
          <Copy size={14} /> Copy link
        </button>
      </div>

      {pending.length > 0 && (
        <div className="space-y-3" data-testid="pending-applications">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-[#291B2D]">Pending applications</h2>
            <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-[#FBF3E1] text-[#B9822B]">{pending.length}</span>
          </div>
          {pending.map((t) => (
            <div key={t.id} data-testid={`pending-row-${t.id}`} className="bg-[#FBF7EF] rounded-2xl border border-[#E8D9BC] p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#C89B55] text-white flex items-center justify-center font-heading shrink-0">{(t.name || "T").slice(0, 1)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#291B2D] truncate">{t.name} <span className="text-xs text-[#8A838E]">{t.credentials}</span></p>
                <p className="text-xs text-[#8A838E] truncate">{t.email || "no email"} · {toStr(t.specialties) || "No specialties"} · {toStr(t.states) || "—"}</p>
              </div>
              <button onClick={() => openEdit(t)} title="Review / edit" data-testid={`pending-view-${t.id}`} className="w-9 h-9 rounded-full bg-white hover:bg-[#f3ece0] flex items-center justify-center"><Pencil size={15} /></button>
              <button onClick={() => approve(t)} data-testid={`pending-approve-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#291B2D] text-white px-4 py-2 text-xs font-medium hover:brightness-110"><Check size={14} /> Approve</button>
              <button onClick={() => reject(t)} data-testid={`pending-reject-${t.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#9B2C2C] px-4 py-2 text-xs font-medium hover:bg-[#FBEAEA]"><X size={14} /> Reject</button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#8A838E]"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : others.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 text-center card-soft" data-testid="therapists-empty">
          <p className="font-heading text-lg">No providers yet</p>
          <p className="text-sm text-[#291B2D]/60 mt-1">Add your first provider — it appears in the directory once published.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {others.map((t) => (
            <div key={t.id} data-testid={`therapist-row-${t.id}`} className="bg-white rounded-2xl border border-[#E5DED4] p-4 flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#291B2D] text-[#C89B55] flex items-center justify-center font-heading shrink-0">{(t.name || "T").slice(0, 1)}</div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#291B2D] truncate">{t.name} <span className="text-xs text-[#8A838E]">{t.credentials}</span></p>
                <p className="text-xs text-[#8A838E] truncate">{toStr(t.specialties) || "No specialties"} · {toStr(t.states) || "—"}</p>
              </div>
              <span className={`text-[10px] uppercase tracking-wide rounded-full px-2 py-1 ${t.published ? "bg-[#EEF5F1] text-[#3D6B52]" : "bg-[#F7F2EA] text-[#8A838E]"}`}>{t.published ? "Published" : "Draft"}</span>
              <button onClick={() => togglePublish(t)} title={t.published ? "Unpublish" : "Publish"} data-testid={`therapist-publish-${t.id}`} className="w-9 h-9 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center">{t.published ? <EyeOff size={15} /> : <Eye size={15} />}</button>
              <button onClick={() => openEdit(t)} title="Edit" data-testid={`therapist-edit-${t.id}`} className="w-9 h-9 rounded-full bg-[#F7F2EA] hover:bg-[#efe7d9] flex items-center justify-center"><Pencil size={15} /></button>
              <button onClick={() => remove(t)} title="Delete" data-testid={`therapist-delete-${t.id}`} className="w-9 h-9 rounded-full bg-[#FBEAEA] hover:bg-[#f6dede] text-[#9B2C2C] flex items-center justify-center"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="therapist-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{dialog?.id ? "Edit" : "Add"} provider</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Name</Label><Input data-testid="th-name" value={dialog.name} onChange={(e) => setField("name", e.target.value)} placeholder="Dr. Jane Doe" /></div>
                <div><Label>Credentials</Label><Input data-testid="th-credentials" value={dialog.credentials} onChange={(e) => setField("credentials", e.target.value)} placeholder="LCSW, PhD" /></div>
              </div>
              <div>
                <Label>Headshot</Label>
                <div className="flex items-center gap-3">
                  {dialog.photo ? (
                    <img src={mediaUrl(dialog.photo)} alt="preview" className="w-14 h-14 rounded-xl object-cover border border-[#E5DED4]" data-testid="th-photo-preview" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-[#F7F2EA] flex items-center justify-center text-[#C89B55] font-heading">{(dialog.name || "T").slice(0, 1)}</div>
                  )}
                  <input ref={photoRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="th-photo-file" />
                  <button type="button" onClick={() => photoRef.current?.click()} disabled={uploading} data-testid="th-photo-upload" className="inline-flex items-center gap-2 rounded-full bg-[#F7F2EA] text-[#291B2D] px-4 py-2 text-sm hover:brightness-95 disabled:opacity-50">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {dialog.photo ? "Replace" : "Upload"}
                  </button>
                  {dialog.photo && <button type="button" onClick={() => setField("photo", "")} className="text-xs text-[#9B2C2C] hover:underline">Remove</button>}
                </div>
                <Input data-testid="th-photo" value={dialog.photo} onChange={(e) => setField("photo", e.target.value)} placeholder="…or paste an image URL" className="mt-2" />
              </div>
              <div><Label>Bio</Label><Textarea data-testid="th-bio" rows={3} value={dialog.bio} onChange={(e) => setField("bio", e.target.value)} placeholder="Warm, 2-3 sentence introduction." /></div>
              <div><Label>Specialties (comma-separated)</Label><Input data-testid="th-specialties" value={dialog.specialties} onChange={(e) => setField("specialties", e.target.value)} placeholder="Anxiety, PTSD & Trauma, Racial Identity" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>States licensed (comma-separated)</Label><Input data-testid="th-states" value={dialog.states} onChange={(e) => setField("states", e.target.value)} placeholder="GA, NY" /></div>
                <div><Label>Languages (comma-separated)</Label><Input data-testid="th-languages" value={dialog.languages} onChange={(e) => setField("languages", e.target.value)} placeholder="English, Spanish" /></div>
              </div>
              <div><Label>Insurance (comma-separated)</Label><Input data-testid="th-insurance" value={dialog.insurance} onChange={(e) => setField("insurance", e.target.value)} placeholder="Aetna, Sliding Scale" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Format</Label>
                  <Select value={dialog.modality} onValueChange={(v) => setField("modality", v)}>
                    <SelectTrigger data-testid="th-modality"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="telehealth">Telehealth</SelectItem>
                      <SelectItem value="in-person">In-person</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-5 pt-6">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="th-faith" checked={dialog.faith_informed} onChange={(e) => setField("faith_informed", e.target.checked)} /> Faith-informed</label>
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="th-trauma" checked={dialog.trauma_informed} onChange={(e) => setField("trauma_informed", e.target.checked)} /> Trauma-informed</label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Booking / Website URL</Label><Input data-testid="th-booking" value={dialog.booking_url} onChange={(e) => setField("booking_url", e.target.value)} placeholder="https://…" /></div>
                <div><Label>Email</Label><Input data-testid="th-email" value={dialog.email} onChange={(e) => setField("email", e.target.value)} placeholder="hello@example.com" /></div>
              </div>
              <div><Label>Phone (optional)</Label><Input data-testid="th-phone" value={dialog.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="1-800-000-0000" /></div>
              <label className="flex items-center gap-2 text-sm pt-1"><input type="checkbox" data-testid="th-published" checked={dialog.published} onChange={(e) => setField("published", e.target.checked)} /> Published (visible in directory)</label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="therapist-dialog-save" className="bg-[#291B2D] text-white hover:bg-[#3a2740]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulk} onOpenChange={setBulk}>
        <DialogContent data-testid="bulk-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Bulk import providers</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-[#59535B]">Paste CSV rows or upload a .csv file. First row must be the header. For multi-value columns (specialties, states, languages, insurance) separate values with a semicolon (;).</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={downloadTemplate} data-testid="bulk-template" className="inline-flex items-center gap-2 rounded-full bg-[#F7F2EA] text-[#291B2D] px-4 py-2 text-sm hover:brightness-95"><Download size={14} /> Download template</button>
              <input ref={csvRef} type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" data-testid="bulk-file" />
              <button onClick={() => csvRef.current?.click()} data-testid="bulk-upload" className="inline-flex items-center gap-2 rounded-full bg-[#F7F2EA] text-[#291B2D] px-4 py-2 text-sm hover:brightness-95"><FileUp size={14} /> Upload .csv</button>
            </div>
            <Textarea data-testid="bulk-text" rows={10} value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"name,credentials,specialties,states,modality,published\nDr. Jane Doe,LCSW,Anxiety;PTSD & Trauma,GA;NY,both,true"} className="font-mono text-xs" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulk(false)}>Cancel</Button>
            <Button onClick={runImport} disabled={importing} data-testid="bulk-import-run" className="bg-[#291B2D] text-white hover:bg-[#3a2740]">{importing ? "Importing…" : "Import"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
